import { forwardRef, type ButtonHTMLAttributes, type HTMLAttributes } from 'react';
import { cn } from '../../lib/utils';

/**
 * The strip of section tabs a page is read through — boulders / routes /
 * boards on a gym, insights / sends / created climbs on a profile. One look
 * everywhere, so the two pages read as one app: the tabs share the width
 * while they fit (`flex-1`) and keep their own once they don't (`shrink-0` +
 * nowrap), and the strip scrolls sideways instead of overflowing the screen.
 */
export const SectionTabs = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex bg-muted rounded-lg p-1 overflow-x-auto [scrollbar-width:none]', className)}
      {...props}
    />
  )
);
SectionTabs.displayName = 'SectionTabs';

interface SectionTabProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  active: boolean;
}

/**
 * One tab of the strip. `data-tab-active` is what a strip that has to scroll
 * the selected tab into view looks for (see ClimbsList).
 */
export const SectionTab = forwardRef<HTMLButtonElement, SectionTabProps>(
  ({ active, className, type = 'button', ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      data-tab-active={active}
      className={cn(
        'flex-1 shrink-0 whitespace-nowrap px-3 py-1 text-sm rounded-md transition-colors',
        active ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
        className
      )}
      {...props}
    />
  )
);
SectionTab.displayName = 'SectionTab';
